import api from '../../services/api';
import { invalidateProductSelectionCatalog } from './productSelectionApi';

/**
 * API client for user-managed product categories ("Kategoria": Płyta OSB,
 * Regips, Płytki). Distinct from fetchProductCategories() in ./api, which
 * returns the fixed enum classification values.
 */

export interface ProductCategory {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  dimensionDisplayProfile?: string | null;
  productCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductCategoryPayload {
  name: string;
  description?: string | null;
  color?: string | null;
  dimensionDisplayProfile?: string | null;
}

export interface ProductCategoryAssignedProduct {
  id: string;
  code?: string | null;
  name: string;
  englishName?: string | null;
  ean?: string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  productCategoryId?: string | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  isActive?: boolean;
}

// Single home for mapping a backend category error to a translated (Polish-first)
// message. The backend sends a stable `code`; the English `message` is never
// shown to the user. Unknown/uncoded errors fall back to the caller's string.
export function categoryErrorMessage(
  error: any,
  t: (key: string) => string,
  fallback: string
): string {
  const code = error?.response?.data?.code;
  if (code === 'DUPLICATE_CATEGORY_NAME') {
    return t('products:productCategory.duplicateName');
  }
  if (code === 'STALE_PRODUCT_CATEGORY') {
    return t('products:productCategory.staleCategory');
  }
  if (code === 'STALE_PRODUCT_SELECTION') {
    return t('products:productCategory.staleProductSelection');
  }
  if (code === 'INVALID_DIMENSION_DISPLAY_PROFILE') {
    return t('products:productCategory.invalidDimensionDisplayProfile');
  }
  return fallback;
}

export async function fetchProductCategoryTags() {
  const response = await api.get<ProductCategory[]>('/product-categories');
  return response.data;
}

export async function createProductCategoryTag(payload: ProductCategoryPayload) {
  const response = await api.post<ProductCategory>('/product-categories', payload);
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function updateProductCategoryTag(id: string, payload: ProductCategoryPayload) {
  const response = await api.put<ProductCategory>(`/product-categories/${id}`, payload);
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function deleteProductCategoryTag(id: string) {
  await api.delete(`/product-categories/${id}`);
  invalidateProductSelectionCatalog();
}

export async function fetchProductCategoryAssignments(id: string) {
  const response = await api.get<ProductCategoryAssignedProduct[]>(`/product-categories/${id}/products`);
  return response.data;
}

export async function fetchProductCategoryAssignmentCandidates() {
  const response = await api.get<ProductCategoryAssignedProduct[]>('/product-categories/assignment-candidates');
  return response.data;
}

export async function replaceProductCategoryAssignments(id: string, productIds: string[]) {
  const response = await api.put<ProductCategory>(`/product-categories/${id}/products`, { productIds });
  invalidateProductSelectionCatalog();
  return response.data;
}
